import { v4 as uuid } from "uuid";

export const DISTANCE_SCALE = {
  mtrs: {
    name: "Mtrs",
    id: "mtrs",
    scale: 1,
  },
  miles: {
    name: "Miles",
    id: "miles",
    scale: 1609.34,
  },
  kms: {
    name: "Kms",
    id: "kms",
    scale: 1000,
  },
};

const buildOptionsDict = (values) =>
  values.reduce((result, value) => {
    result[value] = {
      id: value,
      name: `${value}`,
    };
    return result;
  }, {});

export const LINES_CLASS_LIST = buildOptionsDict(["Overhead", "Underground"]);
export const OPERATIONAL_STATUS_LIST = buildOptionsDict([
  "Decommissioned",
  "Non-Operational",
  "Operational",
  "Committed",
  "Announced",
  "Proposed",
]);
export const CAPACITY_KV_LIST = buildOptionsDict([
  66, 110, 132, 220, 275, 330, 400, 500,
]);
export const STATE_LIST = buildOptionsDict([
  "Queensland",
  "New South Wales",
  "Australian Capital Territory",
  "Victoria",
  "Tasmania",
  "South Australia",
  "Western Australia",
  "Northern Territory",
]);
export const SUBSTATION_CLASS_LIST = buildOptionsDict([
  "Transformer",
  "Substation",
  "Switchyard",
  "Zone",
  "Sole Use",
  "distillate",
]);

export const GENERATOR_CLASSES = Object.freeze(new Set([
  "battery", "solar_utility", "hydro", "gas_ocgt", "bioenergy_biogas", "wind",
  "distillate", "gas_wcmg", "coal_brown", "gas_ccgt", "gas_recip", "gas_steam",
  "bioenergy_biomass", "coal_black", "pumps", "solar", "nuclear", "geothermal",
]));

export const getPointCategory = (feature) => {
  const props = feature?.properties || {};
  if (props["6_digit_bus_number"] !== undefined && props["6_digit_bus_number"] !== null) {
    return "bus";
  }
  const raw = [
    props.FEATURETYPE, props.featuretype, props.FeatureType,
    props.TYPE, props.type, props.Type,
    props.SUBTYPE, props.subtype, props.SubType,
    props.CLASS, props.class, props.Class,
    props.CATEGORY, props.category, props.Category,
  ]
    .filter(Boolean)
    .map((value) => `${value}`.toLowerCase())
    .join(" ");
  if (raw.includes("substation") || raw.includes("transformer")) return "substation";
  if (raw.includes("generator") || raw.includes("power") || raw.includes("plant")) {
    return "generator";
  }
  const classVal = (props.class || props.CLASS || props.Class || "").toLowerCase();
  if (classVal && GENERATOR_CLASSES.has(classVal)) return "generator";
  return "unknown";
};

export const POINT_TYPE_LIST = Object.freeze({
  substation: {
    id: "substation",
    name: "Substation",
  },
  generator: {
    id: "generator",
    name: "Generator",
  },
  bus: {
    id: "bus",
    name: "Bus",
  },
});

export const POINT_SUBSTATION_FIELDS = Object.freeze([
  {
    key: "CLASS",
    label: "Class",
    placeholder: "Select class",
    options: SUBSTATION_CLASS_LIST,
    type: "select",
  },
  {
    key: "OPERATIONALSTATUS",
    label: "Operational Status",
    placeholder: "Select status",
    options: OPERATIONAL_STATUS_LIST,
    type: "select",
  },
  {
    key: "STATE",
    label: "State",
    placeholder: "Select state",
    options: STATE_LIST,
    type: "select",
  },
  {
    key: "VOLTAGEKV",
    label: "Voltage (kV)",
    placeholder: "Enter voltage",
    type: "number",
  },
  {
    key: "LOCALITY",
    label: "Locality",
    placeholder: "Enter locality",
    type: "text",
  },
]);

export const POINT_GENERATOR_FIELDS = Object.freeze([
  {
    key: "FUELTYPE",
    label: "Fuel Type",
    placeholder: "Enter fuel type",
    type: "text",
  },
  {
    key: "CAPACITYMW",
    label: "Capacity (MW)",
    placeholder: "Enter capacity",
    type: "number",
  },
  {
    key: "OPERATIONALSTATUS",
    label: "Operational Status",
    placeholder: "Select status",
    options: OPERATIONAL_STATUS_LIST,
    type: "select",
  },
  {
    key: "STATE",
    label: "State",
    placeholder: "Select state",
    options: STATE_LIST,
    type: "select",
  },
  {
    key: "OWNER",
    label: "Owner",
    placeholder: "Enter owner",
    type: "text",
  },
]);

export const LINE_PROPERTY_FIELDS = Object.freeze([
  {
    key: "CLASS",
    label: "Class",
    placeholder: "Select class",
    options: LINES_CLASS_LIST,
    type: "select",
  },
  {
    key: "OPERATIONALSTATUS",
    label: "Operational Status",
    placeholder: "Select status",
    options: OPERATIONAL_STATUS_LIST,
    type: "select",
  },
  {
    key: "CAPACITYKV",
    label: "Capacity (kV)",
    placeholder: "Select capacity",
    options: CAPACITY_KV_LIST,
    type: "select",
  },
  {
    key: "STATE",
    label: "State",
    placeholder: "Select state",
    options: STATE_LIST,
    type: "select",
  },
  {
    key: "REVISED",
    label: "Revised",
    placeholder: "Auto-updated on edit",
    type: "text",
  },
  {
    key: "CREATED_BY",
    label: "Created by",
    placeholder: "Enter creator",
    type: "text",
  },
]);

export const KRATA_API_TOKEN = "KRATA_API_TOKEN";
export const KRATA_ACTIVE_ORGANISATION = "KRATA_ACTIVE_ORGANISATION";

export const PROJECT_ACTIONS = {
  UPDATE_STATE: "UPDATE_STATE",
  UPDATE_TITLE: "UPDATE_TITLE",
  UPDATE_FEATURES: "UPDATE_FEATURES",
  UPDATE_FEATURE_ORDER: "UPDATE_FEATURE_ORDER",
  DELETE_FEATURES: "DELETE_FEATURES",
  UPDATE_DESCRIPTION: "UPDATE_DESCRIPTION",
  UPDATE_COVER: "UPDATE_COVER",
};

export const GITHUB_AUTH_URL = `https://github.com/login/oauth/authorize?client_id=${
  process.env.VUE_APP_GITHUB_CLIENT_ID
}&redirect_uri=https://open.krata.app/auth/login&state=github_${uuid()}&scope=user`;

export const GOOGLE_AUTH_URL = `https://accounts.google.com/o/oauth2/auth/identifier?client_id=${
  process.env.VUE_APP_GAUTH_CLIENT_ID
}&prompt=consent&redirect_uri=${process.env.VUE_APP_URL}/auth/login&state=google_${uuid()}&response_type=code&scope=profile email&service=lso&o2v=1&flowName=GeneralOAuthFlow`;
